import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DocumentArrowDownIcon, CheckCircleIcon } from '@heroicons/react/24/outline';


const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || `\${API_BASE_URL}`;

interface ExportRequest {
  id: string;
  requestedAt: string;
  status: 'pending' | 'ready' | 'expired' | 'failed';
  downloadUrl?: string;
}

const ExportDataPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState<ExportRequest[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch(`\${API_BASE_URL}/api/settings/export-data`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setRequests(data.requests || []);
      }
    } catch (error) {
      console.error('Failed to load export requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestExport = async () => {
    setSubmitting(true);
    setSuccess(false);
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`\${API_BASE_URL}/api/settings/export-data`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setSuccess(true);
        await fetchRequests();
        setTimeout(() => setSuccess(false), 3000);
      }
    } catch (error) {
      console.error('Failed to request export:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <button
        onClick={() => navigate('/dashboard/settings')}
        className="text-sm text-indigo-600 hover:text-indigo-500 mb-4"
      >
        ← Back to Settings
      </button>
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Export Your Data</h1>
      <p className="mb-6 text-sm text-gray-600">
        Request a copy of your account data for download (GDPR compliant)
      </p>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <button
          onClick={handleRequestExport}
          disabled={submitting}
          className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
        >
          {submitting ? 'Requesting...' : 'Request Data Export'}
        </button>
        {success && (
          <div className="flex items-center mt-4 p-3 bg-green-50 rounded-md">
            <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2" />
            <span className="text-sm text-green-800">Export request submitted!</span>
          </div>
        )}
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Export History</h2>
        {requests.length === 0 ? (
          <div className="text-gray-500 text-sm">No export requests yet</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {requests.map((req) => (
              <li key={req.id} className="py-3 flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-900">
                    Requested: {new Date(req.requestedAt).toLocaleString()}
                  </div>
                  <div className="text-xs text-gray-500 capitalize">
                    Status: {req.status}
                  </div>
                </div>
                {req.status === 'ready' && req.downloadUrl && (
                  <a
                    href={req.downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-3 py-1.5 bg-indigo-600 text-white rounded-md text-xs hover:bg-indigo-700"
                  >
                    <DocumentArrowDownIcon className="w-4 h-4 mr-1" /> Download
                  </a>
                )}
                {req.status === 'expired' && (
                  <span className="text-xs text-red-600">Expired</span>
                )}
                {req.status === 'failed' && (
                  <span className="text-xs text-red-600">Failed</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default ExportDataPage;
